/**
 * Rental module (půjčovna) hooks — pobočky for now (pujcovna-plan P1-d). Server: `/v1/pujcovna/*`,
 * gated by `pujcovna` (read lists, admin mutates).
 */

import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getAuthHeaders } from '@/lib/auth';
import type { components, paths } from '@/types/api';
import { dalsiOffset } from './platby-helpers';

export interface PujcovnaPobocka {
  id: number;
  nazev: string;
  adresa: string;
  email: string;
  telefon: string;
  /** List order; the first pobočka is the default. */
  poradi: number;
  /** Public URL key, unique across all firmas; null = not set (the pobočka is not listed on the web). */
  slug: string | null;
  /** Pickup / return hours, at most one interval per weekday; empty = none (not listed on the web). */
  otviraci_doba: OtviraciDoba[];
}

/** One weekday's opening interval; `den` is ISO (1 = Monday … 7 = Sunday), times are `HH:mm`. */
export interface OtviraciDoba {
  den: number;
  od: string;
  do: string;
}

export const pujcovnaKeys = {
  pobocky: ['pujcovna', 'pobocky'] as const,
  navrhy: ['pujcovna', 'pobocky', 'navrhy'] as const,
  /**
   * The fleet list (and the enrolment car search under it). A fleet row carries the denormalized
   * pobočka / tarif / kategorie names, so renaming or deleting one of those must refresh it too.
   */
  vozidla: ['pujcovna', 'vozidla'] as const,
};

/** A name offered when creating a pobočka (the firma's LMS locations not yet used). A starting point, not a link. */
export interface PobockaNavrh {
  nazev: string;
  zdroj: 'lms';
  /** The firma's seat, only on the main LMS location (an LMS location has no address of its own). */
  adresa: string;
}

export function usePobockaNavrhyQuery(enabled = true) {
  return useQuery({
    queryKey: pujcovnaKeys.navrhy,
    enabled,
    queryFn: ({ signal }) => api.get<{ navrhy: PobockaNavrh[] }>('/v1/pujcovna/pobocky/navrhy', { signal }),
    select: (data) => data.navrhy,
  });
}

export function usePobockyQuery() {
  return useQuery({
    queryKey: pujcovnaKeys.pobocky,
    queryFn: ({ signal }) => api.get<{ pobocky: PujcovnaPobocka[] }>('/v1/pujcovna/pobocky', { signal }),
    select: (data) => data.pobocky,
  });
}

/** Slice-only: every field optional on update, `nazev` required on create. */
export interface PobockaWriteBody {
  nazev?: string;
  adresa?: string;
  email?: string;
  telefon?: string;
  poradi?: number;
  /** '' clears the URL key. */
  slug?: string;
  otviraci_doba?: OtviraciDoba[];
}

/** A write changes the list and the module's change history. */
function useInvalidatePobocky() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: pujcovnaKeys.pobocky });
    qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

export function useCreatePobockaMutation() {
  const invalidate = useInvalidatePobocky();
  return useMutation({
    mutationFn: (body: PobockaWriteBody & { nazev: string }) =>
      api.post<{ pobocka: PujcovnaPobocka }>('/v1/pujcovna/pobocky', body),
    onSuccess: invalidate,
  });
}

export function useUpdatePobockaMutation(id: number) {
  const invalidate = useInvalidatePobocky();
  return useMutation({
    mutationFn: (body: PobockaWriteBody) =>
      api.put<{ pobocka: PujcovnaPobocka }>(`/v1/pujcovna/pobocky/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePobockaMutation() {
  const invalidate = useInvalidatePobocky();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/pujcovna/pobocky/${id}`),
    onSuccess: invalidate,
  });
}

// ---------- Tarify (per firma: name, VOP link and the pricing — markup, length bands, seasons) ----------

export type PujcovnaTarif = components['schemas']['PujcovnaTarif'];
export type TarifWriteBody = components['schemas']['PujcovnaTarifUpdate'];

const tarifyKey = ['pujcovna', 'tarify'] as const;

export function useTarifyQuery(enabled = true) {
  return useQuery({
    queryKey: tarifyKey,
    enabled,
    queryFn: ({ signal }) => api.get<{ tarify: PujcovnaTarif[] }>('/v1/pujcovna/tarify', { signal }),
    select: (data) => data.tarify,
  });
}

/** A write changes the list and the module's change history. */
function useInvalidateTarify() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: tarifyKey });
    qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

export function useCreateTarifMutation() {
  const invalidate = useInvalidateTarify();
  return useMutation({
    mutationFn: (body: TarifWriteBody & { nazev: string }) =>
      api.post<{ tarif: PujcovnaTarif }>('/v1/pujcovna/tarify', body),
    onSuccess: invalidate,
  });
}

export function useUpdateTarifMutation(id: number) {
  const invalidate = useInvalidateTarify();
  return useMutation({
    mutationFn: (body: TarifWriteBody) => api.put<{ tarif: PujcovnaTarif }>(`/v1/pujcovna/tarify/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteTarifMutation() {
  const invalidate = useInvalidateTarify();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/pujcovna/tarify/${id}`),
    onSuccess: invalidate,
  });
}

// ---------- Insurers and their replacement-car rates (P5-d) ----------

export type PujcovnaPojistovna = components['schemas']['PujcovnaPojistovna'];
export type PojistovnaWriteBody = components['schemas']['PujcovnaPojistovnaUpdate'];

const pojistovnyKey = ['pujcovna', 'pojistovny'] as const;

export function usePojistovnyQuery(enabled = true) {
  return useQuery({
    queryKey: pojistovnyKey,
    enabled,
    queryFn: ({ signal }) => api.get<{ pojistovny: PujcovnaPojistovna[] }>('/v1/pujcovna/pojistovny', { signal }),
    select: (data) => data.pojistovny,
  });
}

/** A write changes the list, the module's change history and the price a reservation would get. */
function useInvalidatePojistovny() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: pojistovnyKey });
    qc.invalidateQueries({ queryKey: ['pujcovna', 'cena'] });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

export function useCreatePojistovnaMutation() {
  const invalidate = useInvalidatePojistovny();
  return useMutation({
    mutationFn: (body: PojistovnaWriteBody & { nazev: string }) => api.post<{ pojistovna: PujcovnaPojistovna }>('/v1/pujcovna/pojistovny', body),
    onSuccess: invalidate,
  });
}

export function useUpdatePojistovnaMutation(id: number) {
  const invalidate = useInvalidatePojistovny();
  return useMutation({
    mutationFn: (body: PojistovnaWriteBody) => api.put<{ pojistovna: PujcovnaPojistovna }>(`/v1/pujcovna/pojistovny/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePojistovnaMutation() {
  const invalidate = useInvalidatePojistovny();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/pujcovna/pojistovny/${id}`),
    onSuccess: invalidate,
  });
}

// ---------- Web categories (platform-wide; Platforma, platform admins only) ----------

export interface PujcovnaKategorie {
  id: number;
  nazev: string;
  /** Opaque icon slug the public site maps to an image; '' = none. */
  ikona: string;
  poradi: number;
  /** Public URL key, unique across categories; null = not set (the category is not listed on the web). */
  slug: string | null;
}

export interface KategorieWriteBody {
  nazev?: string;
  ikona?: string;
  poradi?: number;
  /** '' clears the URL key. */
  slug?: string;
}

const KATEGORIE_PATH = '/v1/pujcovna-kategorie';
const kategorieKey = ['pujcovna', 'kategorie', 'platforma'] as const;
const kategorieNabidkaKey = ['pujcovna', 'kategorie', 'nabidka'] as const;

/** A category write changes the platform list, the list the fleet form offers and the names on fleet rows. */
function useInvalidateKategorie() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: kategorieKey });
    qc.invalidateQueries({ queryKey: kategorieNabidkaKey });
    qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
  };
}

export function useKategorieQuery(enabled = true) {
  return useQuery({
    queryKey: kategorieKey,
    enabled,
    queryFn: ({ signal }) => api.get<{ kategorie: PujcovnaKategorie[] }>(KATEGORIE_PATH, { signal }),
    select: (data) => data.kategorie,
  });
}

export function useCreateKategorieMutation() {
  const invalidate = useInvalidateKategorie();
  return useMutation({
    mutationFn: (body: KategorieWriteBody & { nazev: string }) =>
      api.post<{ kategorie: PujcovnaKategorie }>(KATEGORIE_PATH, body),
    onSuccess: invalidate,
  });
}

export function useUpdateKategorieMutation(id: number) {
  const invalidate = useInvalidateKategorie();
  return useMutation({
    mutationFn: (body: KategorieWriteBody) =>
      api.put<{ kategorie: PujcovnaKategorie }>(`${KATEGORIE_PATH}/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteKategorieMutation() {
  const invalidate = useInvalidateKategorie();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`${KATEGORIE_PATH}/${id}`),
    onSuccess: invalidate,
  });
}

// ---------- Fleet (vozidla půjčovny; list read, enrol / edit / remove write) ----------

export type FleetEntry = components['schemas']['PujcovnaVozidlo'];
export type FleetUpdateBody = components['schemas']['PujcovnaVozidloUpdate'];
export type FleetEnrollBody = components['schemas']['PujcovnaVozidloEnroll'];
export type DirectoryVehicle = components['schemas']['DirectoryVehicle'];

const fleetKey = pujcovnaKeys.vozidla;

export function useFleetQuery(enabled = true) {
  return useQuery({
    queryKey: fleetKey,
    enabled,
    queryFn: ({ signal }) => api.get<{ vozidla: FleetEntry[] }>('/v1/pujcovna/vozidla', { signal }),
    select: (data) => data.vozidla,
  });
}

/** The platform-wide web categories as the fleet form offers them (read-only for every půjčovna member). */
export function useKategorieNabidkaQuery(enabled = true) {
  return useQuery({
    queryKey: kategorieNabidkaKey,
    enabled,
    queryFn: ({ signal }) => api.get<{ kategorie: PujcovnaKategorie[] }>('/v1/pujcovna/kategorie', { signal }),
    select: (data) => data.kategorie,
  });
}

/**
 * The group's cars matching a plate or VIN (the read-only directory, admitted for půjčovna write): the
 * enrolment picker. Each row is already the car's canonical live row; an ambiguous VIN comes back flagged.
 */
export function useFleetCarSearchQuery(q: string, enabled = true) {
  const term = q.trim();
  return useQuery({
    queryKey: ['pujcovna', 'vozidla', 'hledani', term] as const,
    enabled: enabled && term.length >= 2,
    queryFn: ({ signal }) =>
      api.get<{ vehicles: DirectoryVehicle[] }>(`/v1/vozidla-skupiny?q=${encodeURIComponent(term)}&limit=15`, {
        signal,
      }),
    select: (data) => data.vehicles,
  });
}

// ---------- Insurances of a fleet car (read at read, save at write) ----------

export type PojisteniBlok = components['schemas']['PujcovnaPojisteniBlok'];
export type PojisteniTyp = PojisteniBlok['typ'];

export const pojisteniKey = (id: number) => ['pujcovna', 'pojisteni', id] as const;

/** All five blocks of one fleet car, always in the server's fixed order. Fresh on every open: it is edited from a modal. */
export function usePojisteniQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: pojisteniKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<{ pojisteni: PojisteniBlok[] }>(`/v1/pujcovna/vozidla/${id}/pojisteni`, { signal }),
    select: (data) => data.pojisteni,
  });
}

export function useSavePojisteniMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pojisteni: PojisteniBlok[]) =>
      api.put<{ pojisteni: PojisteniBlok[] }>(`/v1/pujcovna/vozidla/${id}/pojisteni`, { pojisteni }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pojisteniKey(id) });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
    },
  });
}

// ---------- Damage map of a fleet car (read at read, set / clear at write) ----------

export type Zavada = components['schemas']['PujcovnaZavada'];
export type Zavaznost = Zavada['zavaznost'];
export type ZavadaBody = components['schemas']['PujcovnaZavadaBody'];
export type ZavadaOpravena = components['schemas']['PujcovnaZavadaOpravena'];
/** The newest repaired faults (at most 200) and how many there are in all. */
export type ZavadyOpravene = components['schemas']['PujcovnaZavadyOpravene'];
export type ZavadyPoZnovu = components['schemas']['PujcovnaZavadyPoZnovuOtevreni'];

export type Dil = components['schemas']['PujcovnaDil'];
export type ZonaDilu = Dil['zona'];

export const zavadyKey = (id: number) => ['pujcovna', 'zavady', id] as const;
/** Nested under `zavadyKey`: invalidating the active faults refetches the repaired ones too (prefix match). */
export const opraveneZavadyKey = (id: number) => ['pujcovna', 'zavady', id, 'opravene'] as const;

/** The selectable car parts (exterior 1..92, interior 101+) with their names and cabin groups; static data. */
export function useDilyQuery(enabled = true) {
  return useQuery({
    queryKey: ['pujcovna', 'dily'] as const,
    enabled,
    staleTime: Infinity,
    queryFn: ({ signal }) => api.get<{ dily: Dil[] }>('/v1/pujcovna/dily', { signal }),
    select: (data) => data.dily,
  });
}

/** The current faults of one fleet car. Fresh on every open: it is edited from a modal. */
export function useZavadyQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: zavadyKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<{ zavady: Zavada[] }>(`/v1/pujcovna/vozidla/${id}/zavady`, { signal }),
    select: (data) => data.zavady,
  });
}

/** The repaired faults of one fleet car (they stay on the car), newest repair first. Fresh on every open. */
export function useOpraveneZavadyQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: opraveneZavadyKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<ZavadyOpravene>(`/v1/pujcovna/vozidla/${id}/opravene-zavady`, { signal }),
  });
}

/** Every write changes the active faults and / or the repaired ones, so both lists are re-read (and the history). */
function useZavadyWrite<V, R>(id: number, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: zavadyKey(id) });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
    },
  });
}

export function useSetZavadaMutation(id: number) {
  return useZavadyWrite(id, (v: { dil: number; body: ZavadaBody }) =>
    api.put<{ zavady: Zavada[] }>(`/v1/pujcovna/vozidla/${id}/zavady/${v.dil}`, v.body),
  );
}

/** The day-to-day way out of a fault: it is repaired and stays on the car (write). */
export function useRepairZavadaMutation(id: number) {
  return useZavadyWrite(id, (v: { dil: number; poznamka: string }) =>
    api.post<{ zavady: Zavada[] }>(`/v1/pujcovna/vozidla/${id}/zavady/${v.dil}/oprava`, { poznamka: v.poznamka }),
  );
}

/** Delete a fault entered by mistake — leaves no record, admin only. */
export function useClearZavadaMutation(id: number) {
  return useZavadyWrite(id, (dil: number) => api.delete<{ zavady: Zavada[] }>(`/v1/pujcovna/vozidla/${id}/zavady/${dil}`));
}

/** Reopen a repaired fault: back on the map as it was (409 while the part has another active fault). */
export function useReopenZavadaMutation(id: number) {
  return useZavadyWrite(id, (oid: number) =>
    api.post<ZavadyPoZnovu>(`/v1/pujcovna/vozidla/${id}/opravene-zavady/${oid}/znovu`, {}),
  );
}

// ---------- History of a fleet car (read at read): one timeline joined from what is stored ----------

export type VozidloUdalost = components['schemas']['PujcovnaVozidloUdalost'];
export type VozidloHistorie = components['schemas']['PujcovnaVozidloHistorie'];
export type HistorieSkupina = NonNullable<paths['/v1/pujcovna/vozidla/{id}/historie-vozu']['get']['parameters']['query']>['skupina'];

export const HISTORIE_VOZU_PAGE = 50;
export const historieVozuKey = (id: number) => ['pujcovna', 'historie-vozu', id] as const;

/** The timeline of one fleet car, newest first, paged; `skupina` narrows it to one kind of events. Fresh on every open. */
export function useHistorieVozuQuery(id: number, skupina: HistorieSkupina | undefined, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...historieVozuKey(id), skupina ?? 'vse'] as const,
    enabled,
    staleTime: 0,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => {
      const qs = new URLSearchParams({ limit: String(HISTORIE_VOZU_PAGE), offset: String(pageParam) });
      if (skupina) qs.set('skupina', skupina);
      return api.get<VozidloHistorie>(`/v1/pujcovna/vozidla/${id}/historie-vozu?${qs.toString()}`, { signal });
    },
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.udalosti.length, 0);
      return loaded < last.celkem && last.udalosti.length > 0 ? loaded : undefined;
    },
  });
}

// ---------- Odometer series of a fleet car (read at read, record at write) ----------

export type Odecet = components['schemas']['PujcovnaOdecet'];
export type OdectyResponse = components['schemas']['PujcovnaOdecty'];
export type OdecetBody = components['schemas']['PujcovnaOdecetBody'];

export const tachometrKey = (id: number) => ['pujcovna', 'tachometr', id] as const;

/** The readings of one fleet car and the stock car's mileage. Fresh on every open: it is read from a modal. */
export function useTachometrQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: tachometrKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<OdectyResponse>(`/v1/pujcovna/vozidla/${id}/tachometr`, { signal }),
  });
}

/**
 * A reading may become the stock car's mileage (the owner's row, possibly another firma's), so beside the series
 * it refreshes the fleet list (its Nájezd column), both stocks' lists and details, and the change histories.
 */
export function useAddOdecetMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OdecetBody) => api.post<OdectyResponse>(`/v1/pujcovna/vozidla/${id}/tachometr`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tachometrKey(id) });
      qc.invalidateQueries({ queryKey: fleetKey });
      qc.invalidateQueries({ queryKey: kontrolyTerminyKey }); // a newer reading can make a km check due
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['historie'] });
    },
  });
}

// ---------- Checks of a fleet car (read at read, add / edit / complete / remove at write) ----------

export type Kontrola = components['schemas']['PujcovnaKontrola'];
export type KontrolaStav = Kontrola['stav'];
export type KontrolyResponse = components['schemas']['PujcovnaKontroly'];
export type KontrolaBody = components['schemas']['PujcovnaKontrolaBody'];
export type KontrolaSplnitBody = components['schemas']['PujcovnaKontrolaSplnit'];

export const kontrolyKey = (id: number) => ['pujcovna', 'kontroly', id] as const;

/** The checks of one fleet car with their computed state. Fresh on every open: it is edited from a modal. */
export function useKontrolyQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: kontrolyKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly`, { signal }),
  });
}

export type KontrolaTermin = components['schemas']['PujcovnaKontrolaTermin'];
/**
 * A CHILD of the fleet key on purpose: the feed is a projection of the active fleet and the cars' mileage, so
 * every place that invalidates the fleet (stock edits of a car's km, handover, transfer, group changes, fleet
 * forms) refreshes it too, without each having to know this key.
 */
export const kontrolyTerminyKey = [...pujcovnaKeys.vozidla, 'kontroly-terminy'] as const;

/** The checks of the whole fleet that are overdue or upcoming (dashboard card). */
export function useKontrolyTerminyQuery(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: kontrolyTerminyKey,
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) => api.get<components['schemas']['PujcovnaKontrolyTerminy']>('/v1/pujcovna/kontroly/terminy', { signal }),
  });
}

/** Every write answers with all of the car's checks (re-judged), which is what the modal shows next. */
function useKontrolyWrite<V>(id: number, fn: (v: V) => Promise<KontrolyResponse>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      qc.setQueryData(kontrolyKey(id), res);
      qc.invalidateQueries({ queryKey: kontrolyKey(id) });
      qc.invalidateQueries({ queryKey: kontrolyTerminyKey });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
    },
  });
}

export type ServisPrehled = KontrolyResponse['servis'];

/** The km interval of the servisní prohlídka and the km of the last one; answers with the car's re-judged list. */
export function useSetServisIntervalMutation(id: number) {
  return useKontrolyWrite(id, (body: { interval_km: number | null; posledni_km?: number | null }) =>
    api.put<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/servis-interval`, body),
  );
}

export function useAddKontrolaMutation(id: number) {
  return useKontrolyWrite(id, (body: KontrolaBody) => api.post<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly`, body));
}

export function useUpdateKontrolaMutation(id: number) {
  return useKontrolyWrite(id, (v: { kid: number; body: KontrolaBody }) =>
    api.put<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly/${v.kid}`, v.body),
  );
}

export function useCompleteKontrolaMutation(id: number) {
  return useKontrolyWrite(id, (v: { kid: number; body: KontrolaSplnitBody }) =>
    api.post<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly/${v.kid}/splneni`, v.body),
  );
}

export function useReopenKontrolaMutation(id: number) {
  return useKontrolyWrite(id, (kid: number) => api.delete<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly/${kid}/splneni`));
}

export function useRemoveKontrolaMutation(id: number) {
  return useKontrolyWrite(id, (kid: number) => api.delete<KontrolyResponse>(`/v1/pujcovna/vozidla/${id}/kontroly/${kid}`));
}

// ---------- Photos of a fleet car: the car's OWN gallery + the rental's choice ----------

export type Foto = components['schemas']['PujcovnaFoto'];
export type FotkyResponse = components['schemas']['PujcovnaFotky'];

export const fotkyKey = (id: number) => ['pujcovna', 'fotky', id] as const;

/**
 * The car's gallery (signed URLs, valid for minutes) with the rental's choice. Fresh on every open: it is edited
 * from a modal, and a cached snapshot's URLs may have expired.
 */
export function useFotkyQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: fotkyKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<FotkyResponse>(`/v1/pujcovna/vozidla/${id}/fotky`, { signal }),
  });
}

/** Every write answers with the whole gallery, which is what the modal shows next. */
function useFotkyWrite<V>(id: number, fn: (v: V) => Promise<FotkyResponse>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      qc.setQueryData(fotkyKey(id), res);
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
      // an upload lands in the CAR's gallery: its detail card shows it too
      qc.invalidateQueries({ queryKey: ['fotky'] });
    },
  });
}

/** Surface the API's Czech error message instead of a raw JSON blob. */
async function fotoError(res: Response): Promise<never> {
  const text = await res.text();
  let message = text || `HTTP ${res.status}`;
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (parsed && typeof parsed.error === 'string') message = parsed.error;
  } catch {
    // not JSON — keep the raw text
  }
  throw new Error(message);
}

/** Add a photo to the car's own gallery (and to the rental's choice). */
export function useUploadFotoMutation(id: number) {
  return useFotkyWrite(id, async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/v1/pujcovna/vozidla/${id}/fotky`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) await fotoError(res);
    return (await res.json()) as FotkyResponse;
  });
}

/** Choose which of the car's photos the rental shows, in order (the first is the cover). */
export function useChooseFotkyMutation(id: number) {
  return useFotkyWrite(id, (keys: string[]) =>
    api.put<FotkyResponse>(`/v1/pujcovna/vozidla/${id}/fotky/vyber`, { keys }),
  );
}

// ---------- Reservations (read at read, create / change / cancel at write, booking through a collision at admin) ----------

export type Rezervace = components['schemas']['PujcovnaRezervace'];
// (the generator emits a reused nullable schema as `X | null` in its own definition: the types here are the non-null shapes)
export type CenaRozpis = NonNullable<components['schemas']['PujcovnaCenaRozpis']>;
export type RucniSleva = NonNullable<components['schemas']['PujcovnaRucniSleva']>;
export type CenaDotaz = components['schemas']['PujcovnaCenaDotaz'];
export type CenaOdpoved = components['schemas']['PujcovnaCenaOdpoved'];

/** The price a rental WOULD have (nothing is saved). `dotaz` null = nothing to price yet. The caller debounces the inputs. */
export function useCenaQuery(dotaz: CenaDotaz | null) {
  return useQuery({
    queryKey: ['pujcovna', 'cena', dotaz] as const,
    enabled: dotaz != null,
    staleTime: 0,
    queryFn: ({ signal }) => api.post<CenaOdpoved>('/v1/pujcovna/cena', dotaz, { signal }),
  });
}
export type RezervaceBody = components['schemas']['PujcovnaRezervaceBody'];
export type RezervaceKonflikt = NonNullable<components['schemas']['PujcovnaRezervaceKonflikt']['konflikty']>[number];

export const rezervaceKey = ['pujcovna', 'rezervace'] as const;

export interface RezervaceFilter {
  od?: string;
  do?: string;
  id_vozidla?: number;
  stav?: 'aktivni' | 'zrusena';
  /** Search: every word must match the number, the customer, the VIN or the car (plate, make, model). */
  q?: string;
}

export const REZERVACE_PAGE = 100;

/** The firma's reservations, newest pickup first, filtered server-side and paged (`total` = the whole selection). */
export function useRezervaceQuery(filter: RezervaceFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...rezervaceKey, filter] as const,
    enabled,
    staleTime: 0,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(filter)) if (v !== undefined && v !== '') qs.set(k, String(v));
      qs.set('limit', String(REZERVACE_PAGE));
      qs.set('offset', String(pageParam));
      return api.get<{ rezervace: Rezervace[]; total: number }>(`/v1/pujcovna/rezervace?${qs.toString()}`, { signal });
    },
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.rezervace.length, 0);
      return loaded < last.total && last.rezervace.length > 0 ? loaded : undefined;
    },
  });
}

export type Prehled = components['schemas']['PujcovnaPrehled'];

/** Today's board: what is out, late, due back and due out (the server judges it at Prague's now); refreshed every minute. */
export function usePrehledQuery(enabled = true) {
  return useQuery({
    // under the reservations' key: a handover / return / cancel refreshes the board with the rest
    queryKey: [...rezervaceKey, 'prehled'] as const,
    enabled,
    staleTime: 0,
    refetchInterval: 60_000,
    queryFn: ({ signal }) => api.get<Prehled>('/v1/pujcovna/prehled', { signal }),
  });
}

/**
 * One car's active reservations that are not over — ending on `od` or later, or out past their end — newest pickup first: what the car's
 * own card names (the one in progress and the next). A plain read (not paged: a car has a few), `od` = Prague's today.
 */
export function useRezervaceVozidlaQuery(idVozidla: number | null, od: string) {
  return useQuery({
    queryKey: [...rezervaceKey, 'vozidlo', idVozidla, od] as const,
    enabled: idVozidla != null,
    staleTime: 0,
    queryFn: ({ signal }) =>
      api.get<{ rezervace?: Rezervace[]; total?: number }>(`/v1/pujcovna/rezervace?id_vozidla=${idVozidla}&stav=aktivni&od=${od}&limit=50`, { signal }),
    select: (d): Rezervace[] => d.rezervace ?? [],
  });
}

export type Dostupnost = components['schemas']['PujcovnaDostupnost'];
export interface DostupnostDotaz {
  id_vozidla: number;
  /** `YYYY-MM-DD HH:mm` (a date alone = the whole day). */
  od: string;
  do: string;
  /** The reservation being changed. */
  ignore?: number;
}

/**
 * One car's reservations around a term and which collide with it (the booking form's availability strip). Never served
 * from the cache (`gcTime` 0): a stale answer must not pass for the car's availability. `dotaz` null = nothing to ask yet.
 */
export function useDostupnostQuery(dotaz: DostupnostDotaz | null) {
  return useQuery({
    queryKey: ['pujcovna', 'dostupnost', dotaz] as const,
    enabled: dotaz != null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    // the strip stays drawn (dimmed) while the answer for a changed term is on its way; callers judge `isPlaceholderData`
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ id_vozidla: String(dotaz!.id_vozidla), od: dotaz!.od, do: dotaz!.do });
      if (dotaz!.ignore != null) qs.set('ignore', String(dotaz!.ignore));
      return api.get<Dostupnost>(`/v1/pujcovna/dostupnost?${qs.toString()}`, { signal });
    },
  });
}

function useInvalidateRezervace() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: rezervaceKey });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

/** One reservation (the printouts open from a link, so they read it themselves). Fresh: it is a document. */
export function useRezervaceDetailQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: [...rezervaceKey, 'detail', id] as const,
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<{ rezervace: Rezervace }>(`/v1/pujcovna/rezervace/${id}`, { signal }),
    select: (data) => data.rezervace,
  });
}

/** Create (no id) or change (id) a reservation. A collision is an ApiError 409 whose body carries `konflikty`. */
export function useSaveRezervaceMutation() {
  const invalidate = useInvalidateRezervace();
  return useMutation({
    mutationFn: (v: { id?: number; body: RezervaceBody }) =>
      v.id == null
        ? api.post<{ rezervace: Rezervace }>('/v1/pujcovna/rezervace', v.body)
        : api.put<{ rezervace: Rezervace }>(`/v1/pujcovna/rezervace/${v.id}`, v.body),
    onSuccess: invalidate,
  });
}

export function useCancelRezervaceMutation() {
  const invalidate = useInvalidateRezervace();
  return useMutation({
    mutationFn: (v: { id: number; duvod?: string }) =>
      api.post<{ rezervace: Rezervace }>(`/v1/pujcovna/rezervace/${v.id}/zruseni`, { duvod: v.duvod ?? '' }),
    onSuccess: invalidate,
  });
}

// ---------- Drivers of a reservation + the handover (P4-d) ----------

export type RezervaceRidic = components['schemas']['PujcovnaRezervaceRidic'];
export type PredaniPriprava = components['schemas']['PujcovnaPredaniPriprava'];
export type PredaniVstup = components['schemas']['PujcovnaPredaniVstup'];
export type PredaniSeznam = components['schemas']['PujcovnaPredaniSeznam'];
export type PujcovnaPredani = components['schemas']['PujcovnaPredani'];
export type VraceniVstup = components['schemas']['PujcovnaVraceniVstup'];

export const rezervaceRidiciKey = (id: number) => ['pujcovna', 'rezervace-ridici', id] as const;
export const predaniPripravaKey = (id: number) => ['pujcovna', 'predani-priprava', id] as const;
export const predaniKey = (id: number) => ['pujcovna', 'predani', id] as const;

/** The drivers named on a reservation (names only — the documents stay on the customer). Always re-read on open. */
export function useRezervaceRidiciQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: rezervaceRidiciKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<{ ridici: RezervaceRidic[] }>(`/v1/pujcovna/rezervace/${id}/ridici`, { signal }),
  });
}

/** Replace the WHOLE set of drivers of a reservation; the pre-flight (`chybi`) depends on it, so it is refreshed too. */
export function useSaveRezervaceRidiciMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ridici: Array<{ id_ridice: number; hlavni: boolean }>) =>
      api.put<{ ridici: RezervaceRidic[] }>(`/v1/pujcovna/rezervace/${id}/ridici`, { ridici }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rezervaceRidiciKey(id) });
      qc.invalidateQueries({ queryKey: predaniPripravaKey(id) });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
    },
  });
}

/** The advisory pre-flight of a handover / return: the phase, what is missing, the km to prefill. Never cached. */
export function usePredaniPripravaQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: predaniPripravaKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<PredaniPriprava>(`/v1/pujcovna/rezervace/${id}/predani/priprava`, { signal }),
  });
}

/** Hand the car over. A handover changes the reservation, the odometer series, the fleet's mileage and the history. */
export function usePredatMutation(id: number, idVozidla: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PredaniVstup) => api.post<{ predani: PujcovnaPredani }>(`/v1/pujcovna/rezervace/${id}/predani`, body),
    onSettled: () => {
      // a refused handover leaves the pre-flight stale too (the 409 may have been about what was missing)
      qc.invalidateQueries({ queryKey: predaniPripravaKey(id) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rezervaceKey });
      qc.invalidateQueries({ queryKey: predaniKey(id) });
      qc.invalidateQueries({ queryKey: tachometrKey(idVozidla) });
      qc.invalidateQueries({ queryKey: zavadyKey(idVozidla) });
      qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
      // the reading written with the protocol is the stock car's mileage too: its detail and lists must not show the old one
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/** The protocols of a reservation (handover first) with the frozen renter / lessor. Fresh on every open. */
export function usePredaniQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: predaniKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<PredaniSeznam>(`/v1/pujcovna/rezervace/${id}/predani`, { signal }),
  });
}

/**
 * Take the car back. A return changes the reservation (state, maybe a shortened end), the odometer series, the car's
 * damage map and mileage, and the history.
 */
export function useVratitMutation(id: number, idVozidla: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VraceniVstup) => api.post<{ predani: PujcovnaPredani }>(`/v1/pujcovna/rezervace/${id}/vraceni`, body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: predaniPripravaKey(id) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rezervaceKey });
      qc.invalidateQueries({ queryKey: predaniKey(id) });
      qc.invalidateQueries({ queryKey: tachometrKey(idVozidla) });
      qc.invalidateQueries({ queryKey: zavadyKey(idVozidla) });
      qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
      // the reading written with the protocol is the stock car's mileage too: its detail and lists must not show the old one
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

// ---------- Photos of a handover / return: evidence of one rental (P4-f) ----------

export type PredaniTyp = 'vydani' | 'vraceni';
export type PredaniFotkyData = components['schemas']['PujcovnaPredaniFotky'];

export const predaniFotkyKey = (id: number, typ: PredaniTyp) => ['pujcovna', 'predani-fotky', id, typ] as const;

/** The photos of one side of a protocol (signed URLs, valid for minutes). Fresh on every open. */
export function usePredaniFotkyQuery(id: number, typ: PredaniTyp, enabled = true) {
  return useQuery({
    queryKey: predaniFotkyKey(id, typ),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<PredaniFotkyData>(`/v1/pujcovna/rezervace/${id}/predani/${typ}/fotky`, { signal }),
  });
}

/** Every write answers with the whole set, which is what the strip shows next. */
function usePredaniFotkyWrite<V>(id: number, typ: PredaniTyp, fn: (v: V) => Promise<PredaniFotkyData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => qc.setQueryData(predaniFotkyKey(id, typ), res),
    // a refused change (the protocol was written meanwhile) leaves the strip stale: re-read it
    onError: () => qc.invalidateQueries({ queryKey: predaniFotkyKey(id, typ) }),
  });
}

export function useUploadPredaniFotoMutation(id: number, typ: PredaniTyp) {
  return usePredaniFotkyWrite(id, typ, async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/v1/pujcovna/rezervace/${id}/predani/${typ}/fotky`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) await fotoError(res);
    return (await res.json()) as PredaniFotkyData;
  });
}

export function useRemovePredaniFotoMutation(id: number, typ: PredaniTyp) {
  return usePredaniFotkyWrite(id, typ, (klic: string) =>
    api.delete<PredaniFotkyData>(`/v1/pujcovna/rezervace/${id}/predani/${typ}/fotky?klic=${encodeURIComponent(klic)}`),
  );
}

/** A fleet write changes the list and the module's change history. */
function useInvalidateFleet() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: fleetKey });
    // The dashboard feed covers the ACTIVE fleet: enrolling / editing / taking a car out changes it.
    qc.invalidateQueries({ queryKey: kontrolyTerminyKey });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

export function useEnrollFleetMutation() {
  const invalidate = useInvalidateFleet();
  return useMutation({
    mutationFn: (body: FleetEnrollBody) => api.post<{ id: number }>('/v1/pujcovna/vozidla', body),
    onSuccess: invalidate,
  });
}

export function useUpdateFleetMutation(id: number) {
  const invalidate = useInvalidateFleet();
  return useMutation({
    mutationFn: (body: FleetUpdateBody) => api.put<{ status: 'ok' }>(`/v1/pujcovna/vozidla/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useRemoveFleetMutation() {
  const invalidate = useInvalidateFleet();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/pujcovna/vozidla/${id}`),
    onSuccess: invalidate,
  });
}

// ==================== SETTINGS (turnaround gap) ====================

/** The firma's rental settings: the turnaround gap, the price groups (% of the maximum) and the loyalty threshold; null = not configured. */
export type PujcovnaNastaveni = components['schemas']['PujcovnaNastaveni'];

const nastaveniKey = ['pujcovna', 'nastaveni'] as const;

export function useNastaveniQuery(enabled = true) {
  return useQuery({
    queryKey: nastaveniKey,
    enabled,
    // The form seeds from this and a stale value would be saved over the real one: always refetch on mount.
    refetchOnMount: 'always',
    queryFn: ({ signal }) => api.get<PujcovnaNastaveni>('/v1/pujcovna/nastaveni', { signal }),
  });
}

export function useSaveNastaveniMutation() {
  const qc = useQueryClient();
  return useMutation({
    /** Slice-only: just the fields that changed. */
    mutationFn: (body: Partial<PujcovnaNastaveni>) => api.put<PujcovnaNastaveni>('/v1/pujcovna/nastaveni', body),
    onSuccess: (data) => {
      qc.setQueryData(nastaveniKey, data);
      // the fleet's daily prices are computed from the price groups: a saved group must not leave the list showing the old prices
      qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
      qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
    },
  });
}

// ---------- Payment gateway of the firma (P6-a) ----------

export type PujcovnaBrana = components['schemas']['PujcovnaPlatebniBrana'];
export type PujcovnaPoskytovatel = components['schemas']['PujcovnaPoskytovatelPlateb'];
export type BranaCreateBody = components['schemas']['PujcovnaBranaCreateBody'];
export type BranyData = components['schemas']['PujcovnaBranyList'];

const branyKey = ['pujcovna', 'brany'] as const;

/** Admin only on the server; the list carries no secret, only which secret fields are stored. */
export function useBranyQuery(enabled = true) {
  return useQuery({
    queryKey: branyKey,
    enabled,
    queryFn: ({ signal }) => api.get<BranyData>('/v1/pujcovna/brany', { signal }),
  });
}

function useInvalidateBrany() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: branyKey });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

export function useCreateBranaMutation() {
  const invalidate = useInvalidateBrany();
  return useMutation({
    mutationFn: (body: BranaCreateBody) => api.post<{ brana: PujcovnaBrana }>('/v1/pujcovna/brany', body),
    onSuccess: invalidate,
  });
}

export function useVypnoutBranuMutation() {
  const invalidate = useInvalidateBrany();
  return useMutation({
    mutationFn: (id: number) => api.post<{ brana: PujcovnaBrana }>(`/v1/pujcovna/brany/${id}/vypnuti`, {}),
    onSuccess: invalidate,
  });
}

// ---------- Payments of a reservation and the exception queue (P6-e2) ----------

export type PlatbyPrehled = components['schemas']['PujcovnaPlatbyPrehled'];
export type Platba = components['schemas']['PujcovnaPlatba'];
export type PlatbaVraceni = components['schemas']['PujcovnaPlatbaVraceni'];
export type Uhrada = components['schemas']['PujcovnaUhrada'];
export type RucniUhradaBody = components['schemas']['PujcovnaRucniUhradaBody'];
export type VraceniBody = components['schemas']['PujcovnaVraceniBody'];
export type PlatbaVyjimka = components['schemas']['PujcovnaPlatbaVyjimka'];
export type VyjimkaStav = PlatbaVyjimka['stav'];
export type VyjimkaDruh = PlatbaVyjimka['druh'];
export type VyjimkaVyrizeniBody = components['schemas']['PujcovnaVyjimkaVyrizeniBody'];

/** Under the reservations' key: any reservation mutation (a repriced lease, a cancellation) refreshes the open payments too. */
export const platbyKey = (id: number) => [...rezervaceKey, 'platby', id] as const;
const vyjimkyKey = ['pujcovna', 'platby-vyjimky'] as const;

/** One reservation's payments: the balance, the gateway attempts with their refunds, the credits. Fresh on open — it is money. */
export function usePlatbyQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: platbyKey(id),
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<PlatbyPrehled>(`/v1/pujcovna/rezervace/${id}/platby`, { signal }),
  });
}

/** What changes after money moves: this reservation's payments, the list's paid column, the queue and the history. */
function useInvalidatePlatby() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: rezervaceKey });
    qc.invalidateQueries({ queryKey: vyjimkyKey });
    qc.invalidateQueries({ queryKey: ['historie', 'pujcovna'] });
  };
}

/** A payment taken at the branch (cash, terminal): at `pujcovna:write`. */
export function useRucniUhraduMutation(id: number) {
  const invalidate = useInvalidatePlatby();
  return useMutation({
    mutationFn: (body: RucniUhradaBody) => api.post<{ uhrada_id: number; zbyva: number }>(`/v1/pujcovna/rezervace/${id}/uhrady`, body),
    onSuccess: invalidate,
  });
}

/** Refund (part of) one gateway attempt: `pujcovna:admin`. `klic` makes a repeat of the same request return the first outcome. */
export function useVratitPlatbuMutation(idPlatby: number) {
  const invalidate = useInvalidatePlatby();
  return useMutation({
    mutationFn: (body: VraceniBody) => api.post<{ id_vraceni: number; stav: 'vraceno' | 'ceka' }>(`/v1/pujcovna/platby/${idPlatby}/vraceni`, body),
    onSuccess: invalidate,
  });
}

export const VYJIMKY_PAGE = 100;

/** How many items wait in the queue: the tab's badge, one cheap row instead of a page of them. */
export function useVyjimkyPocetQuery(enabled = true) {
  return useQuery({
    queryKey: [...vyjimkyKey, 'pocet'] as const,
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<{ vyjimky: PlatbaVyjimka[]; otevrene: number }>('/v1/pujcovna/platby/vyjimky?stav=otevrena&limit=1', { signal }),
    select: (d) => d.otevrene,
  });
}

export type UhradaPrehled = components['schemas']['PujcovnaUhrady']['uhrady'][number];
export interface UhradyFilter {
  od: string;
  do: string;
  zdroj: '' | 'platba' | 'rucne' | 'import';
}
export const UHRADY_PAGE = 100;

/**
 * The úhrady across reservations (read), newest first, paged; `celkem` / `soucet` describe everything the filter matches. The key sits under the
 * reservations' key, so any reservation or payment mutation refreshes the overview.
 */
export function useUhradyQuery(f: UhradyFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...rezervaceKey, 'uhrady-prehled', f] as const,
    enabled,
    staleTime: 0,
    placeholderData: keepPreviousData, // a changed filter keeps the rows and totals on screen until the new ones arrive
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => {
      const qs = new URLSearchParams({ limit: String(UHRADY_PAGE), offset: String(pageParam) });
      if (f.od) qs.set('od', f.od);
      if (f.do) qs.set('do', f.do);
      if (f.zdroj) qs.set('zdroj', f.zdroj);
      return api.get<components['schemas']['PujcovnaUhrady']>(`/v1/pujcovna/uhrady?${qs.toString()}`, { signal });
    },
    getNextPageParam: (last, all) => dalsiOffset(last.uhrady.length, all.map((p) => p.uhrady.length), UHRADY_PAGE),
  });
}

/** The exception queue (admin), oldest open first / newest settled first, paged; `otevrene` counts the open ones whatever the filter. */
export function useVyjimkyQuery(stav: VyjimkaStav, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...vyjimkyKey, stav] as const,
    enabled,
    staleTime: 0,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      api.get<{ vyjimky: PlatbaVyjimka[]; otevrene: number }>(`/v1/pujcovna/platby/vyjimky?stav=${stav}&limit=${VYJIMKY_PAGE}&offset=${pageParam}`, { signal }),
    // a full page means there may be more; a short one is the end
    getNextPageParam: (last, all) => dalsiOffset(last.vyjimky.length, all.map((p) => p.vyjimky.length), VYJIMKY_PAGE),
  });
}

/** Accept an item as handled, or refund its money through the gateway (`ceka` = the refund is not confirmed yet; the item stays open). */
export function useVyresitVyjimkuMutation(id: number) {
  const invalidate = useInvalidatePlatby();
  return useMutation({
    mutationFn: (body: VyjimkaVyrizeniBody) => api.post<{ stav: 'vyrizena' | 'ceka' }>(`/v1/pujcovna/platby/vyjimky/${id}/vyrizeni`, body),
    onSuccess: invalidate,
  });
}
